import React, { useState, useEffect } from 'react';
import { AlertCircle, Database, Upload, Settings, Activity, Search, FileText, BarChart3, Wrench, Shield } from 'lucide-react';


// Import existing components
import DatabaseStatus from './components/DatabaseStatus';
import RecordsTable from './components/RecordsTable';
import SearchPanel from './components/SearchPanel';
import AdminControls from './components/AdminControls';
import FileUploadZone from './components/FileUploadZone';
import FacilityDashboard from './components/FacilityDashboard';
import FlexibleSearchPanel from './components/FlexibleSearchPanel';
import ComprehensiveRecordsTable from './components/ComprehensiveRecordsTable';
import UploadDiagnosticsPanel from './components/UploadDiagnosticsPanel';


// Import new diagnostic components
import DatabaseSearchDiagnosticPanel from '../../components/DatabaseSearchDiagnosticPanel';
import CsvIngestionDebugPanel from './components/CsvIngestionDebugPanel';
import ImmediateFixPanel from './components/ImmediateFixPanel';
import UploadHealthDashboard from './components/UploadHealthDashboard';
import BatchProgressMonitor from './components/BatchProgressMonitor';
import Icon from '../../components/AppIcon';
import damianChatService from '../../services/damianChatService';
import { useAuth } from '../../contexts/AuthContext';
import SupabaseDeploymentPanel from '../../components/ui/SupabaseDeploymentPanel';

// Add GlobalNavigation component
import GlobalNavigation from '../../components/GlobalNavigation';

export default function DatabaseManagement() {
  const { user, userProfile, loading, authError } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [activeTab, setActiveTab] = useState('immediate-fix'); // Start with immediate fix tab
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({});

  // Add this block - menu state handler
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleMenuClick = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  // End of added block

  // Enhanced tab configuration with immediate fix as first priority
  const tabs = [
    { 
      id: 'immediate-fix', 
      label: 'Immediate Fix', 
      icon: Shield,
      description: 'Emergency controls and immediate fix plan for stuck CSV uploads'
    },
    { 
      id: 'health-dashboard', 
      label: 'Health Dashboard', 
      icon: Activity,
      description: 'Real-time system health monitoring and upload processing status'
    },
    { 
      id: 'progress-monitor', 
      label: 'Progress Monitor', 
      icon: BarChart3,
      description: 'Live batch progress monitoring with real-time counters'
    },
    { 
      id: 'csv-debug', 
      label: 'CSV Debug Playbook', 
      icon: Wrench,
      description: 'Comprehensive CSV ingestion debugging - identify why thousands become ~9 records'
    },
    { 
      id: 'diagnostics', 
      label: 'Search Diagnostics', 
      icon: Wrench,
      description: 'Run comprehensive diagnostics on database search functions'
    },
    { 
      id: 'dashboard', 
      label: 'Facility Dashboard', 
      icon: BarChart3,
      description: 'Real-time facility statistics and population data'
    },
    { 
      id: 'search', 
      label: 'Flexible Search', 
      icon: Search,
      description: 'Advanced search with fuzzy matching and multiple criteria'
    },
    { 
      id: 'records', 
      label: 'Records Management', 
      icon: FileText,
      description: 'Browse, search, and manage inmate records'
    },
    { 
      id: 'upload', 
      label: 'Data Upload', 
      icon: Upload,
      description: 'Upload and process arrest records from CSV files'
    },
    { 
      id: 'upload-diagnostics', 
      label: 'Upload Diagnostics', 
      icon: Activity,
      description: 'Monitor upload processes and diagnose issues'
    },
    { 
      id: 'admin', 
      label: 'Admin Controls', 
      icon: Settings,
      description: 'System administration and maintenance tools'
    }
  ];

  // Search states
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    total: 0,
    totalPages: 0
  });
  const [sortBy, setSortBy] = useState('last_arrest_datetime');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedPersons, setSelectedPersons] = useState([]);
  const [showPersonHistory, setShowPersonHistory] = useState(false);
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const [lastSearchParams, setLastSearchParams] = useState(null);
  const [searchLimitExceeded, setSearchLimitExceeded] = useState(false);

  // Modal states
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showRecordModal, setShowRecordModal] = useState(false);

  // Error state
  const [error, setError] = useState('');

  // Redirect if not authenticated
  useEffect(() => {
    if (!loading && !user) {
      window.location.href = '/admin-login';
    }
  }, [user, loading]);

  // Set auth errors
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);

  const handleFileUpload = async (result) => {
    if (result?.success) {
      setActiveTab('history');
      // Optionally refresh upload history
    }
  };

  // Updated search function for flexible search with enhanced John search
  const handleSearch = (params) => {
    setSearchParams(params);
    setIsLoading(true);
    // Search logic handled by individual components
  };

  // Add this variable to track if search was performed
  const searchPerformed = searchResults?.length > 0 || lastSearchParams !== null;

  const handleViewRecord = (record) => {
    setSelectedRecord(record);
    setShowRecordModal(true);
  };

  const handleExportResults = (recordIds) => {
    // Implementation for exporting records
    if (recordIds?.length > 0) {
      alert(`Export functionality will be implemented for ${recordIds?.length} record(s)`);
    }
  };

  const handlePageChange = (newPage) => {
    if (lastSearchParams) {
      handleSearch(lastSearchParams, newPage);
    }
  };

  const handleSort = (column, order) => {
    setSortBy(column);
    setSortOrder(order);
    // Note: Sorting is handled by the database function
    if (lastSearchParams) {
      handleSearch(lastSearchParams, 1); // Reset to page 1 when sorting
    }
  };

  const handlePersonSelect = async (selectedIds) => {
    setSelectedPersons(selectedIds);
    
    // Provide feedback through Damian when selections change
    if (searchResults?.length > 0) {
      await damianChatService?.handleRecordSelection?.(selectedIds?.length, searchResults?.length);
    }
  };

  const handleViewHistory = (personId) => {
    setSelectedPersonId(personId);
    setShowPersonHistory(true);
  };

  const handleCloseHistory = () => {
    setShowPersonHistory(false);
    setSelectedPersonId(null);
  };

  const clearError = () => {
    setError('');
    setSearchLimitExceeded(false);
  };

  const stats = {
    totalRecords: 1247,
    todayUploads: 89,
    duplicatesFound: 12,
    systemStatus: 'Online'
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
            <Icon name="Database" size={24} color="white" />
          </div>
          <p className="text-muted-foreground">Loading database management...</p>
        </div>
      </div>
    );
  }

  // Show auth error or no user state
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-error rounded-lg flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={24} color="white" />
          </div>
          <h1 className="text-xl font-bold text-foreground mb-2">Access Denied</h1>
          <p className="text-muted-foreground mb-4">Please sign in to access the database management system.</p>
          <button
            onClick={() => window.location.href = '/admin-login'}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNavigation onMenuClick={handleMenuClick} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Supabase Deployment Panel - NEW */}
        <SupabaseDeploymentPanel />

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Database Management</h1>
          <p className="text-gray-600">
            Comprehensive inmate database management system with advanced search and diagnostics
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex flex-wrap -mb-px">
              {tabs?.map((tab) => {
                const IconComponent = tab?.icon;
                return (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                      flex items-center gap-2 px-6 py-3 border-b-2 font-medium text-sm transition-colors
                      ${activeTab === tab?.id
                        ? 'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <IconComponent className="w-4 h-4" />
                    {tab?.label}
                  </button>
                );
              })}
            </nav>
          </div>
          
          {/* Tab Description */}
          <div className="px-6 py-3 bg-gray-50">
            <p className="text-sm text-gray-600">
              {tabs?.find(t => t?.id === activeTab)?.description}
            </p>
          </div>
        </div>

        {/* Immediate Fix Panel */}
        {activeTab === 'immediate-fix' && (
          <ImmediateFixPanel />
        )}

        {/* Health Dashboard */}
        {activeTab === 'health-dashboard' && (
          <UploadHealthDashboard />
        )}

        {/* Progress Monitor */}
        {activeTab === 'progress-monitor' && (
          <BatchProgressMonitor />
        )}

        {/* CSV Debug Playbook Tab */}
        {activeTab === 'csv-debug' && (
          <div className="space-y-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <h3 className="font-medium text-red-800">CSV Ingestion Issue Investigation</h3>
              </div>
              <p className="text-sm text-red-700 mt-1">
                Systematic 10-step debugging process to identify why bulk CSV uploads report success with thousands of rows 
                but database only shows ~9 records. This comprehensive playbook covers all high-probability root causes.
              </p>
            </div>
            
            <CsvIngestionDebugPanel />
          </div>
        )}

        {/* Search Diagnostics Tab */}
        {activeTab === 'diagnostics' && (
          <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600" />
                <h3 className="font-medium text-yellow-800">Jose Jiminez Search Issue Investigation</h3>
              </div>
              <p className="text-sm text-yellow-700 mt-1">
                Running comprehensive diagnostics to identify and resolve database search issues, 
                focusing on the Jose Jiminez search problem. This panel will test all search functions 
                and database connectivity to ensure Claude has proper access to find inmates.
              </p>
            </div>
            
            <DatabaseSearchDiagnosticPanel />
          </div>
        )}

        {/* Facility Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            <DatabaseStatus />
            <FacilityDashboard />
          </div>
        )}

        {/* Flexible Search Tab */}
        {activeTab === 'search' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <FlexibleSearchPanel onSearch={handleSearch} loading={isLoading} />
            </div>
            <div className="lg:col-span-3">
              <ComprehensiveRecordsTable 
                searchParams={searchParams}
                isLoading={isLoading}
                onLoadingChange={setIsLoading}
              />
            </div>
          </div>
        )}

        {/* Records Management Tab */}
        {activeTab === 'records' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <SearchPanel onSearch={handleSearch} loading={isLoading} />
            </div>
            <div className="lg:col-span-3">
              <RecordsTable 
                searchParams={searchParams}
                isLoading={isLoading}
                onLoadingChange={setIsLoading}
                records={searchResults}
                loading={searchLoading}
                onViewRecord={handleViewRecord}
                onExportResults={handleExportResults}
                pagination={pagination}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        )}

        {/* Data Upload Tab */}
        {activeTab === 'upload' && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900">Data Upload Guidelines</h3>
              <p className="text-sm text-blue-700 mt-1">
                Upload arrest records in CSV format. Files are processed automatically with duplicate detection and data normalization.
              </p>
            </div>
            <FileUploadZone onFileUpload={handleFileUpload} isUploading={isUploading} />
          </div>
        )}

        {/* Upload Diagnostics Tab */}
        {activeTab === 'upload-diagnostics' && (
          <div className="space-y-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="font-medium text-purple-900">Upload Process Monitoring</h3>
              <p className="text-sm text-purple-700 mt-1">
                Monitor file upload progress, analyze processing errors, and review upload history.
              </p>
            </div>
            <UploadDiagnosticsPanel />
          </div>
        )}

        {/* Admin Controls Tab */}
        {activeTab === 'admin' && (
          <div className="space-y-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <h3 className="font-medium text-red-800">Administrative Functions</h3>
              </div>
              <p className="text-sm text-red-700 mt-1">
                System maintenance tools and administrative controls. Use with caution.
              </p>
            </div>
            <AdminControls />
          </div>
        )}
      </div>
    </div>
  );
}